// React
import { FunctionComponent, useState } from "react"

// UI Library
import { Button, Modal, Form } from "react-bootstrap"

// Local types
import {
    I_IncidentAction,
    I_SafetyEvent,
    I_SafetyEventWithApprovalHistory
} from "../../interfaces"

// Services
import VIS from "../../VehicleIncidentsService"
import approvalService from "src/components/approvals/ApprovalService"
import ActionApproval from "src/components/approvals/crud/ActionApproval"
import snackBar from "src/utilities/snackBar/SnackBar"

// Constants
import { getActionTypeId } from "../../constants/VehicleIncidentsApprovalConfig"

// Styles
import "./VehicleIncidentsForwardToNextReviewer.css"
import VehicleIncidentsMiniInfo from "../VehicleIncidentsMiniInfo/VehicleIncidentsMiniInfo"

interface Props {
    incidentAction: I_IncidentAction
}

// Modal for forwarding an event to the next reviewer in the approval chain
const VehicleIncidentsForwardToNextReviewer: FunctionComponent<Props> = ({
    incidentAction
}) => {
    // Pull shared functions from VIS service
    const { setIncidentAction, retrieveEventFromMap, refresh, findStepId } = VIS

    // Local state
    const [comment, setComment] = useState("")         // Optional comment input
    const [busy, setBusy] = useState(false) // Button loading state

    // Retrieve full event details from service cache
    const event: I_SafetyEvent | I_SafetyEventWithApprovalHistory | null =
        retrieveEventFromMap(incidentAction.id || -1)

    // Handle submission of "Forward to Next Reviewer"
    const handleSubmit = async () => {
        
        if (!event) return

        setBusy(true)

        // Attempt to forward approval step
        const success = await ActionApproval({
            ApprovalStepId: findStepId(event),
            ActionedBy: VIS.loggedInEmployeeId,
            ApprovalActionTypeId: getActionTypeId(approvalService.reference, "Forward"),
            Comments: comment || null
        })

        await refresh(VIS.currentView)

        // Show feedback
        if (success) {
            snackBar({ type: "primary", text: "Forwarded to next reviewer.", timeout: 4000 })
        } else {
            snackBar({ type: "error", text: "Failed to forward to next reviewer.", timeout: 4000 })
        }

        setBusy(false)
    }

    return (
        <div className="VehicleIncidentsForwardToNextReviewer">
            <Modal
                id="vehicle-incidents-forward-to-next-reviewer-modal"
                show
                backdrop="static"
                keyboard
                centered
                className="VehicleIncidentsWorkflowModal"
                animation={false}
            >
                <Modal.Header>
                    <div className="VehicleIncidentsForwardToNextReviewerHeader VehicleIncidentsWorkflowModalHeader">
                        Forward to Next Reviewer
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <div className="VehicleIncidentsForwardToNextReviewerModalBody">
                        <VehicleIncidentsMiniInfo
                            id={incidentAction.id}
                        />
                        <Form.Group>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Add any comments for the next reviewer"
                            />
                        </Form.Group>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <div className="VehicleIncidentsForwardToNextReviewerModalFooter VehicleIncidentsGenericModalFooter">
                        <div className="VehicleIncidentsForwardToNextReviewerModalFooterLeft">
                            {/* Goes back to action selection modal */}
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    setIncidentAction({
                                        action: 1,
                                        id: incidentAction.id,
                                    })
                                }
                            >
                                Back
                            </Button>
                        </div>

                        <div className="VehicleIncidentsForwardToNextReviewerModalFooterRight">
                            {/* Submits forward action */}
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={busy}
                            >
                                {busy ? "Forwarding..." : "Forward to Next Reviewer"}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default VehicleIncidentsForwardToNextReviewer